/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useState } from 'react';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import root from 'window-or-global';
import usePrevious from '@/utils/usePrevious';
import { type Locale } from '@/types/i18n';
import { blogPrefix } from '../utils/isValidBlogLocale';

type UseBlogAnalyticsProps = {
  blogData?: {
    url?: string;
  };
  locale: Locale;
};

export const useBlogAnalytics = ({ blogData, locale }: UseBlogAnalyticsProps) => {
  const hasSetupAnalytics = useAnalytics();
  const pageViewCall = usePageViewCall();
  const { pageSpecificData } = usePageSpecificData();
  const [pageViewCalled, setPageViewStatus] = useState(false);
  const prevBlogUrl = usePrevious(blogData?.url);

  useEffect(() => {
    if (prevBlogUrl !== blogData?.url) {
      setPageViewStatus(false);
    }
  }, [blogData?.url, prevBlogUrl]);

  useEffect(() => {
    if (!pageViewCalled && hasSetupAnalytics && blogData?.url) {
      const section = blogPrefix[locale].slice(1);
      const urlPath = blogData.url.replace(`/${section}/`, '') || '';
      const isHomePage = blogData.url === `/${section}/`;
      const cleanedPath = urlPath.replace(/\//g, ':');
      const pageName = isHomePage ? `az:${section}:home` : `az:${section}:${cleanedPath}`;
      const seoPageType = isHomePage
        ? `${section.toUpperCase()}:/home`
        : `${section.toUpperCase()}:/${urlPath}`;

      const currentUrl = root.location.href;
      const documentReferrer = root.document.referrer;
      const isExternalTraffic =
        currentUrl.includes('cmpid') ||
        (!!documentReferrer && !documentReferrer.includes('autozone.com'));

      const pageSpecificAnalyticsData = {
        pageName,
        pageType: pageTypes.blog,
        siteSection: pageTypes.blog,
        pageURL: root.location.href,
        seoPageType,
        ...(isExternalTraffic && { seoEntryPageType: seoPageType }),
        ...pageSpecificData,
      };

      pageViewCall(pageSpecificAnalyticsData);
      setPageViewStatus(true);
    }
  }, [blogData?.url, hasSetupAnalytics, pageViewCall, pageViewCalled, locale, pageSpecificData]);

  return { pageViewCalled };
};
